import { el, clear } from "../util/dom";
import { createAlert, deleteAlert, listAlerts } from "../api/endpoints";
import { ApiError } from "../api/client";
import { formatDisplayRatio, isNormalized, scoreToRatio } from "../displayMode";
import type { AlertResponse } from "../api/types";

export interface AlertsView {
  root: HTMLElement;
  refresh(): Promise<void>;
  setVisible(visible: boolean): void;
  /** Re-render the list and hint in the active face's units (after a scale switch). */
  rerender(): void;
}

/**
 * Parse the threshold the user typed, in the ACTIVE face's units. Raw face:
 * the number IS the ratio. Normalized face: the number is an Option-B level,
 * mapped back through scoreToRatio (monotonic, so the above/below derivation
 * on the returned raw ratio is unchanged). Returns the raw ratio to store,
 * or the error message to show. Exported pure for tests.
 */
export function parseAlertThreshold(
  raw: string,
  normalized: boolean
): { value: number } | { error: string } {
  let value = parseFloat(raw);
  if (normalized) {
    const ratio = scoreToRatio(value);
    if (ratio === null)
      return {
        error:
          "Normalized levels must be >= 1 or <= -1 (e.g. 2.5 or -2.5) — levels between -1 and 1 don't exist on this scale.",
      };
    value = ratio;
  }
  if (!Number.isFinite(value) || value <= 0) {
    return {
      error:
        "Raw ratio thresholds must be positive numbers (e.g. 1.042). Negative levels only exist in the Normalized view — switch the scale to Normalized to set a level like -5.",
    };
  }
  if (value === 1.0) {
    return { error: "An alert exactly at the 1.0 centerline can never fire — set it above or below 1.0." };
  }
  return { value };
}

export function buildAlertsView(onError: (msg: string) => void): AlertsView {
  const listEl = el("div", { class: "alert-list" });
  const input = el("input", {
    type: "number",
    step: "0.001",
    class: "add-alert-input",
  }) as HTMLInputElement;
  const addBtn = el("button", { class: "add-alert-btn" }, ["Add Alert"]);

  const formSection = el("div", {}, [
    el("div", { class: "add-alert-bar" }, [
      el("span", { class: "add-alert-label" }, ["Threshold:"]),
      input,
      addBtn,
    ]),
    listEl,
  ]);
  const loggedOutNotice = el("div", { class: "no-alerts" }, [
    "Log in to create and manage ratio alerts.",
  ]);

  const root = el("div", { class: "alerts-panel" }, [formSection, loggedOutNotice]);
  formSection.style.display = "none";

  // Alert values are stored/created as RAW ratios (the API's units); only
  // ENTRY and DISPLAY are face-aware. Cache the last list so a scale switch
  // can re-render without another API round-trip.
  let lastAlerts: AlertResponse[] = [];

  function reflectFace(): void {
    input.placeholder = isNormalized() ? "2.5 or -2.5" : "1.234";
  }
  reflectFace();

  async function refresh(): Promise<void> {
    try {
      const alerts = await listAlerts();
      renderList(alerts);
    } catch (err) {
      onError(describeError(err));
    }
  }

  function renderList(alerts: AlertResponse[]): void {
    lastAlerts = alerts;
    clear(listEl);
    if (alerts.length === 0) {
      listEl.append(el("div", { class: "no-alerts" }, ["No alerts set."]));
      return;
    }
    const sorted = [...alerts].sort((a, b) => b.value - a.value);
    for (const alert of sorted) {
      const removeBtn = el("button", { class: "remove-alert-btn" }, ["×"]);
      removeBtn.addEventListener("click", () => void handleDelete(alert.id));
      const row = el("div", { class: `alert-item alert-${alert.direction}` }, [
        // formatDisplayRatio is face-aware: raw face = toFixed(2) as before,
        // Normalized face = the signed Option-B level.
        el("span", {}, [`${alert.direction === "above" ? "▲" : "▼"} ${formatDisplayRatio(alert.value)}`]),
        removeBtn,
      ]);
      listEl.append(row);
    }
  }

  async function handleDelete(id: number): Promise<void> {
    try {
      await deleteAlert(id);
      await refresh();
    } catch (err) {
      onError(describeError(err));
    }
  }

  addBtn.addEventListener("click", () => void handleAdd());
  input.addEventListener("keypress", (e) => {
    if (e.key === "Enter") void handleAdd();
  });

  async function handleAdd(): Promise<void> {
    const parsed = parseAlertThreshold(input.value, isNormalized());
    if ("error" in parsed) {
      onError(parsed.error);
      return;
    }
    const value = parsed.value;
    const direction = value > 1.0 ? "above" : "below";
    try {
      await createAlert({ direction, value });
      input.value = "";
      await refresh();
    } catch (err) {
      onError(describeError(err));
    }
  }

  function describeError(err: unknown): string {
    if (err instanceof ApiError) return err.detail;
    return err instanceof Error ? err.message : String(err);
  }

  return {
    root,
    refresh,
    setVisible(visible: boolean): void {
      formSection.style.display = visible ? "" : "none";
      loggedOutNotice.style.display = visible ? "none" : "";
    },
    rerender(): void {
      reflectFace();
      renderList(lastAlerts);
    },
  };
}
